import { useState, useEffect } from "react";
import axios from "axios";
import useAxiosConfig from "../../../../config/useAxiosConfig";

const useData = () => {
  const { loading: axiosLoading, error, postData: apiPost } = useAxiosConfig();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await axios.get("/AdminSchoolsCollection");
      setData(response.data?.rows || []);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching data:", error);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!axiosLoading) {
      fetchData();
    }
  }, [axiosLoading]);

  const postData = async (newData) => {
    try {
      const postData = {
        jsStruct: JSON.stringify(newData),
      };
      const response = await axios.post("/AdminSchoolsCollection", postData);
      setData((prevData) => [...prevData, response.data]);
      return response.data;
    } catch (error) {
      console.error("Error adding data:", error);
      throw error;
    }
  };

  const fetchSchoolinfo = async (schoolId) => {
    try {
      const response = await axios.get(`/AdminSchoolsMember/${schoolId}`);
      if (response.data.success && response.data.rows.length > 0) {
        const schoolInfoData = response.data.rows[0];
        return schoolInfoData || {};
      } else {
        return {};
      }
    } catch (error) {
      console.error("Error fetching School Info data:", error);
      throw error;
    }
  };

  const updateSchoolInfo = async (schoolId, schoolInfo) => {
    try {
      const response = await axios.put(
        `/AdminSchoolsMember/${schoolId}`,
        schoolInfo
      );
      return response.data;
    } catch (error) {
      console.error("Error updating Info:", error);
      throw error;
    }
  };

  const updateSchoolLogo = async (schoolId, schoolInfo) => {
    try {
      const response = await axios.put(
        `/AdminSchoolLogo/${schoolId}`,
        schoolInfo
      );
      return response.data;
    } catch (error) {
      console.error("Error updating Info:", error);
      throw error;
    }
  };

  const fetchMou = async (schoolId) => {
    try {
      const response = await axios.get(`/AdminMouMember/${schoolId}`);
      if (response.data.success && response.data.rows.length > 0) {
        const mouData = response.data.rows[0].mou;
        return mouData || "";
      } else {
        return "";
      }
    } catch (error) {
      console.error("Error fetching MOU:", error);
      throw error;
    }
  };

  const updateMou = async (schoolId, mouData) => {
    try {
      const response = await axios.put(`/AdminMouMember/${schoolId}`, mouData);
      return response.data;
    } catch (error) {
      console.error("Error updating MOU:", error);
      throw error;
    }
  };

  const sendAdminNotification = async (schoolId, subject) => {
    try {
      const response = await axios.put(
        `/AdminNewNotification/${schoolId}`,
        subject
      );
      return response.data;
    } catch (error) {
      console.error("Error Sending Notification:", error);
      throw error;
    }
  };

  const fetchAppTesting = async (schoolId) => {
    try {
      const response = await axios.get(`/AdminAppTestingMember/${schoolId}`);
      if (response.data.success && response.data.rows.length > 0) {
        const appTestingData = response.data.rows[0];
        return appTestingData || {};
      } else {
        return {};
      }
    } catch (error) {
      console.error("Error fetching App Testing data:", error);
      throw error;
    }
  };

  const updateAppTesting = async (schoolId, appTestingData) => {
    try {
      const response = await axios.put(
        `/AdminAppTestingMember/${schoolId}`,
        appTestingData
      );
      return response.data;
    } catch (error) {
      console.error("Error updating App Testing:", error);
      throw error;
    }
  };

  const fetchRegReports = async (schoolId) => {
    try {
      const response = await axios.get(`/AdminRegReportsMember/${schoolId}`);
      if (response.data.success && response.data.rows.length > 0) {
        const regReportsData = response.data.rows[0];
        return regReportsData || {};
      } else {
        return {};
      }
    } catch (error) {
      console.error("Error fetching Registration Reports data:", error);
      throw error;
    }
  };

  const updateRegReports = async (schoolId, regReportsData) => {
    try {
      const response = await axios.put(
        `/AdminRegReportsMember/${schoolId}`,
        regReportsData
      );
      return response.data;
    } catch (error) {
      console.error("Error updating Registration Reports:", error);
      throw error;
    }
  };

  const fetchGradeReports = async (schoolId) => {
    try {
      const response = await axios.get(`/AdminGradeReportsMember/${schoolId}`);
      if (response.data.success && response.data.rows.length > 0) {
        const gradeReportsData = response.data.rows[0];
        return gradeReportsData || {};
      } else {
        return {};
      }
    } catch (error) {
      console.error("Error fetching Grade Reports data:", error);
      throw error;
    }
  };

  const updateGradeReports = async (schoolId, gradeReportsData) => {
    try {
      const response = await axios.put(
        `/AdminGradeReportsMember/${schoolId}`,
        gradeReportsData
      );
      return response.data;
    } catch (error) {
      console.error("Error updating Grade Reports:", error);
      throw error;
    }
  };

  const fetchRestrictedCourses = async (schoolId) => {
    try {
      const response = await axios.get(
        `/AdminRestrictedReportsMember/${schoolId}`
      );
      if (response.data.success && response.data.rows.length > 0) {
        const restrictedCoursesData = response.data.rows[0];
        return restrictedCoursesData || {};
      } else {
        return {};
      }
    } catch (error) {
      console.error("Error fetching Restricted Courses data:", error);
      throw error;
    }
  };

  const updateRestrictedCourses = async (schoolId, restrictedCoursesData) => {
    try {
      const response = await axios.put(
        `/AdminRestrictedReportsMember/${schoolId}`,
        restrictedCoursesData
      );
      return response.data;
    } catch (error) {
      console.error("Error updating Restricted Courses:", error);
      throw error;
    }
  };

  return {
    data,
    loading,
    fetchData,
    fetchSchoolinfo,
    updateSchoolInfo,
    fetchMou,
    updateMou,
    fetchAppTesting,
    updateAppTesting,
    fetchRegReports,
    updateRegReports,
    fetchGradeReports,
    updateGradeReports,
    fetchRestrictedCourses,
    updateRestrictedCourses,
    updateSchoolLogo,
    sendAdminNotification,
    postData,
  };
};

export default useData;
